import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Bot, Play, Plus, Wrench, MessageSquare, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { classifyWorkflowError } from "../lib/workflowErrors";
import { createSequencer } from "../lib/sequencer";
import { presentActionResponse, presentRefusal } from "../features/authority/authorityPresentation";
import { OperatorActionOutcome } from "../features/authority/OperatorActionOutcome";

const OperatorPage = () => {
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [tools, setTools] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [message, setMessage] = useState("");
  const [selectedTool, setSelectedTool] = useState("get_dashboard_stats");
  const [toolInput, setToolInput] = useState("{}");
  const [loading, setLoading] = useState(false);

  /**
   * PERSISTENT, PER-ACTION OUTCOMES.
   *
   * Keyed by action id so two invocations of the SAME tool never share one
   * outcome — the previous code announced `Action ${status}` in a toast, which
   * both vanished and could not distinguish them. `null` id is used for the
   * direct tool-run path before an id exists.
   */
  const [outcomes, setOutcomes] = useState({});

  /**
   * Synchronous double-submit guard, per action id. React state is async, so
   * `disabled={loading}` alone lets two clicks in the same tick both through —
   * and a confirm that fires twice is two consequences from one human decision.
   */
  const inFlight = useRef({});

  /**
   * The RENDERED busy state, per action id.
   *
   * This used to be the page-wide `loading` flag, which meant confirming
   * action A disabled action B's Confirm button. That is not a safety
   * property -- the double-submit guard is `inFlight`, which is synchronous
   * and per action -- it is just a page that freezes while any request is
   * open, and it hid the very interleaving the sequencer exists to survive.
   */
  const [busyActions, setBusyActions] = useState({});
  const markBusy = (actionId, value) =>
    setBusyActions((previous) => ({ ...previous, [actionId]: value }));

  /** One sequencer per session: switching sessions must drop stale answers. */
  const seq = useRef(createSequencer()).current;

  /**
   * `loading` is SHARED across every handler, so it must reflect "is any
   * request still live", not "did the most recent ticket win". Clearing it
   * only when `seq.isCurrent(ticket)` wedged it TRUE forever whenever a
   * superseded handler settled last: the whole page stayed disabled with no
   * request in flight. A counter cannot get that wrong.
   */
  const pending = useRef(0);
  const beginRequest = () => {
    pending.current += 1;
    setLoading(true);
  };
  const endRequest = () => {
    pending.current = Math.max(0, pending.current - 1);
    if (pending.current === 0) setLoading(false);
  };

  const recordOutcome = (key, value) =>
    setOutcomes((previous) => ({ ...previous, [key]: value }));

  const groupedTools = useMemo(() => {
    return tools.reduce((groups, tool) => {
      groups[tool.category] = groups[tool.category] || [];
      groups[tool.category].push(tool);
      return groups;
    }, {});
  }, [tools]);

  const loadSessions = async () => {
    const response = await axios.get(`${API}/operator/sessions`);
    setSessions(response.data);
    if (!activeSessionId && response.data.length > 0) {
      setActiveSessionId(response.data[0].id);
    }
  };

  // A session change invalidates every outcome on screen: leaving the previous
  // session's result visible under a new session would attribute a write to
  // the wrong conversation.
  useEffect(() => {
    seq.next();
    inFlight.current = {};
    setOutcomes({});
    setBusyActions({});
  }, [activeSessionId, seq]);

  /**
   * Ticketed like every other read: a slow detail GET for the OLD session
   * would otherwise land after the new one and overwrite it, showing one
   * session's actions under another session's heading.
   */
  const loadSession = async (sessionId) => {
    if (!sessionId) return;
    const ticket = seq.next();
    const response = await axios.get(`${API}/operator/sessions/${sessionId}`);
    if (!seq.isCurrent(ticket)) return; // a newer session load superseded this
    setActiveSession(response.data);
  };

  const loadTools = async () => {
    const response = await axios.get(`${API}/operator/tools`);
    setTools(response.data.tools || []);
  };

  const loadWorkflows = async () => {
    const response = await axios.get(`${API}/operator/workflows`);
    setWorkflows(response.data.workflows || []);
  };

  useEffect(() => {
    loadSessions();
    loadTools();
    loadWorkflows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSession(activeSessionId);
  }, [activeSessionId]);

  const createSession = async () => {
    beginRequest();
    try {
      const response = await axios.post(`${API}/operator/sessions`, {
        title: "Operator Session",
        context_type: "global",
      });
      setActiveSessionId(response.data.id);
      await loadSessions();
      toast.success("Operator session started");
    } catch (error) {
      toast.error(classifyWorkflowError(error).message);
    } finally {
      endRequest();
    }
  };

  const sendMessage = async () => {
    if (!activeSessionId || !message.trim()) return;
    // The command box is the LLM-router door and the only place a
    // model_proposed hold originates. Two Enter presses in one tick were two
    // routed commands.
    if (inFlight.current.__command) return;
    inFlight.current.__command = true;
    recordOutcome("__command", null);
    beginRequest();
    try {
      const response = await axios.post(`${API}/operator/sessions/${activeSessionId}/commands`, {
        message: message.trim(),
      });
      setMessage("");
      await loadSession(activeSessionId);
      await loadSessions();
      // The command path is where the LLM ROUTER chooses a tool. A
      // model-routed consequential write is HELD, not executed
      // (services/operator_authority.requires_human_confirmation), and it
      // arrives as HTTP 200 with status "needs_confirmation" and
      // result_json.risk === "model_proposed". Announcing that through
      // toast.success as `Operator needs_confirmation` reported a hold in the
      // success colour and then let it vanish.
      if (response.data.action) {
        const routed = response.data.action;
        recordOutcome(routed.id || "__command", {
          outcome: presentActionResponse(routed),
          refusal: null,
          risk: routed.result_json?.risk,
        });
      } else {
        toast.success("Operator responded");
      }
    } catch (error) {
      toast.error(classifyWorkflowError(error).message);
    } finally {
      endRequest();
    }
  };

  /**
   * /approve is the CONFIRM-AND-EXECUTE door for an action the server is
   * holding; /deny settles it without running it. Neither returns a bare
   * success: backend/routes/operator.py commits, refreshes and returns the
   * action body whatever happened, so a governed refusal and a handler failure
   * both arrive as HTTP 200 with status "refused" / "failed". The outcome is
   * READ off that body.
   */
  const decideAction = async (actionId, decision) => {
    if (inFlight.current[actionId]) return; // synchronous: two clicks, one call
    inFlight.current[actionId] = true;
    markBusy(actionId, true);
    const ticket = seq.next();
    beginRequest();
    try {
      const endpoint = decision === "approve" ? "approve" : "deny";
      const response = await axios.post(`${API}/operator/actions/${actionId}/${endpoint}`);
      if (!seq.isCurrent(ticket)) return;
      recordOutcome(actionId, { outcome: presentActionResponse(response.data), refusal: null });
      await loadSession(activeSessionId);
      await loadSessions();
    } catch (error) {
      if (!seq.isCurrent(ticket)) return;
      // A structured refusal carries an OBJECT detail ({code, message}); the
      // previous code passed it straight to toast.error, which renders
      // "[object Object]" for exactly the refusals AUTHORITY-CONTAINMENT-0
      // and -1 introduced. The shared classifier handles both shapes.
      const classified = classifyWorkflowError(error);
      const transport = classified.kind === 'network' || classified.kind === 'server';
      // A network blip or a 500 is NOT a governed refusal, and it must not
      // remove the Confirm/Deny controls: the action is still held, the human
      // still has to decide, and destroying the affordance leaves them with no
      // way to act on it at all. The refusal is shown BESIDE the held outcome
      // rather than replacing it.
      recordOutcome(actionId, {
        outcome: transport ? (outcomes[actionId]?.outcome || null) : null,
        refusal: presentRefusal(classified),
        risk: outcomes[actionId]?.risk,
        transport,
      });
    } finally {
      // Release THIS action's guard whatever the current ticket is. It guards
      // DOUBLE SUBMISSION of this request, not staleness -- staleness is the
      // sequencer's job, and it only decides whether to APPLY a result.
      //
      // Measured: releasing it conditionally wedged the control. Confirm
      // action A, confirm action B before A's response lands, and
      // seq.isCurrent(A's ticket) is already false when A settles -- so A's
      // guard stayed true and A could never be actioned again for the life of
      // the session.
      inFlight.current[actionId] = false;
      markBusy(actionId, false);
      endRequest();
    }
  };

  const runWorkflow = async (workflowName) => {
    beginRequest();
    try {
      const response = await axios.post(`${API}/operator/workflows/${workflowName}/run`, {
        session_id: activeSessionId,
        persist_message: true,
      });
      if (!activeSessionId && response.data.session_id) {
        setActiveSessionId(response.data.session_id);
        await loadSessions();
        await loadSession(response.data.session_id);
      } else {
        await loadSession(activeSessionId);
        await loadSessions();
      }
      toast.success(`${response.data.title} generated`);
    } catch (error) {
      toast.error(classifyWorkflowError(error).message);
    } finally {
      endRequest();
    }
  };

  const runTool = async () => {
    if (!activeSessionId) return;
    let parsedInput = {};
    try {
      parsedInput = JSON.parse(toolInput || "{}");
    } catch {
      toast.error("Tool input must be valid JSON");
      return;
    }

    if (inFlight.current.__runTool) return;
    inFlight.current.__runTool = true;
    const ticket = seq.next();
    beginRequest();
    // Clear the previous direct-run result up front: the panel is keyed to a
    // fixed sentinel, so a refusal recorded on an earlier run would otherwise
    // sit there permanently beside a later success.
    recordOutcome("__runTool", null);
    try {
      const action = await axios.post(`${API}/operator/actions`, {
        session_id: activeSessionId,
        tool_name: selectedTool,
        input_json: parsedInput,
        execute: true,
      });
      if (!seq.isCurrent(ticket)) return;
      const read = presentActionResponse(action.data);
      // Keyed by the real action id so two runs of the same tool stay separate.
      recordOutcome(action.data?.id || "__runTool", { outcome: read, refusal: null, risk: action.data?.result_json?.risk });
      await axios.post(`${API}/operator/sessions/${activeSessionId}/messages`, {
        role: "tool",
        content: `${selectedTool} → ${action.data.status}`,
        metadata_json: { action_id: action.data.id, result: action.data.result_json },
      });
      await loadSession(activeSessionId);
    } catch (error) {
      if (!seq.isCurrent(ticket)) return;
      recordOutcome("__runTool", { outcome: null, refusal: presentRefusal(classifyWorkflowError(error)) });
    } finally {
      // Same rule as decideAction: the guard belongs to this request.
      inFlight.current.__runTool = false;
      endRequest();
    }
  };

  const activeTool = tools.find((tool) => tool.name === selectedTool);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
              <Bot size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Operator</h1>
              <p className="text-muted-foreground">Phase 3 Operator chat: tell the in-app agent what to look up or change, and it routes through safe OMNΛS tools.</p>
            </div>
          </div>
        </div>
        <Button onClick={createSession} disabled={loading} data-testid="operator-new-session">
          <Plus className="mr-2 h-4 w-4" /> New Session
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <Card className="xl:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><MessageSquare size={18} /> Sessions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {sessions.length === 0 && <p className="text-sm text-muted-foreground">No Operator sessions yet.</p>}
            {sessions.map((session) => (
              <button
                key={session.id}
                onClick={() => setActiveSessionId(session.id)}
                className={`w-full text-left rounded-lg border p-3 hover:bg-muted/50 ${activeSessionId === session.id ? "border-primary bg-primary/5" : "border-border"}`}
              >
                <div className="font-medium truncate">{session.title}</div>
                <div className="text-xs text-muted-foreground">{session.context_type} • {session.status}</div>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Conversation Record</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!activeSession && <p className="text-muted-foreground">Create or select a session to begin.</p>}
            {activeSession && (
              <>
                <div className="h-[420px] overflow-y-auto rounded-lg border bg-muted/20 p-4 space-y-3">
                  {(activeSession.messages || []).length === 0 && (
                    <div className="text-sm text-muted-foreground">Ask Operator to look up records or make authorized changes. It uses the LLM to choose from the registered OMNΛS tools, then the server executes permission-checked actions.</div>
                  )}
                  {(activeSession.messages || []).map((item) => (
                    <div key={item.id} className="rounded-lg bg-background border p-3">
                      <div className="flex items-center justify-between mb-1">
                        <Badge variant={item.role === "user" ? "default" : "secondary"}>{item.role}</Badge>
                        <span className="text-xs text-muted-foreground">{item.created_at ? new Date(item.created_at).toLocaleString() : ""}</span>
                      </div>
                      <div className="whitespace-pre-wrap text-sm">{item.content}</div>
                      {item.metadata_json?.result && (
                        <pre className="mt-3 max-h-52 overflow-auto rounded bg-black/80 p-3 text-xs text-white">{JSON.stringify(item.metadata_json.result, null, 2)}</pre>
                      )}
                    </div>
                  ))}
                </div>
                {(activeSession.actions || []).length > 0 && (
                  <div className="rounded-lg border p-3 space-y-2">
                    <div className="text-sm font-semibold">Action status/results</div>
                    {/* NO HEIGHT CAP. This was `max-h-64 overflow-y-auto`,
                        which is fine for a list of labels and wrong for a list
                        of DECISIONS: a single held action's panel -- hold
                        reason, consequence caveat, Confirm and Deny -- is
                        taller than 16rem on its own, so the controls that
                        authorize a consequential write sat below the fold of
                        an inner scroller, on a phone and on a desktop alike.
                        The page already scrolls; the decision must not hide. */}
                    <div data-testid="operator-actions-list" className="space-y-2">
                      {(activeSession.actions || []).map((action) => (
                        <div key={action.id} className="rounded-md bg-muted/30 p-2 text-xs">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant={action.status === "executed" ? "default" : action.status === "failed" ? "destructive" : "secondary"}>{action.status}</Badge>
                            <span className="font-medium">{action.tool_name}</span>
                            <Badge variant="outline">{action.action_type}</Badge>
                          </div>
                          {/* The presented outcome of THIS action, read off
                              its own body and keyed by its own id, so two runs
                              of the same tool never share a result. It
                              persists until the session changes. */}
                          <div className="mt-2">
                            <OperatorActionOutcome
                              outcome={
                                outcomes[action.id] && !outcomes[action.id].transport
                                  ? outcomes[action.id].outcome
                                  : presentActionResponse(action)
                              }
                              refusal={outcomes[action.id]?.refusal || null}
                              holdRisk={outcomes[action.id]?.risk ?? action.result_json?.risk}
                              busy={Boolean(busyActions[action.id])}
                              onConfirm={
                                action.status === "needs_confirmation"
                                  ? () => decideAction(action.id, "approve")
                                  : undefined
                              }
                              onDeny={
                                action.status === "needs_confirmation"
                                  ? () => decideAction(action.id, "deny")
                                  : undefined
                              }
                            />
                          </div>
                          {action.result_json && <pre className="mt-2 max-h-32 overflow-auto rounded bg-black/80 p-2 text-white">{JSON.stringify(action.result_json, null, 2)}</pre>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* MOUNTED WHETHER OR NOT THERE IS A RESULT.
                    This used to render only once an outcome existed, which
                    meant its live region arrived in the same DOM commit as
                    its own first text -- and a direct tool run or a chat
                    command was therefore never announced. The component
                    renders nothing visible until there is something to say.
                    It also covers the action that APPEARS as a result of a
                    chat command: that action's own row mounts with its text
                    already in place, so the row cannot announce itself. */}
                <div data-testid="operator-direct-outcome">
                  <OperatorActionOutcome
                    outcome={(outcomes.__runTool || outcomes.__command)?.outcome || null}
                    refusal={(outcomes.__runTool || outcomes.__command)?.refusal || null}
                    holdRisk={(outcomes.__runTool || outcomes.__command)?.risk}
                  />
                </div>
                <div className="flex gap-2">
                  <Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell Operator what to look up or change..." onKeyDown={(e) => e.key === "Enter" && sendMessage()} />
                  <Button onClick={sendMessage} disabled={loading || !message.trim()}>Send</Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="xl:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Wrench size={18} /> Operator Tools</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-3 text-sm flex gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-500 shrink-0" />
              <span>Write tools enforce roles/tenant ownership and create audit records. Normal edits can execute directly; destructive, award, approval, external, bulk, invite, and role actions require confirmation.</span>
            </div>
            {workflows.length > 0 && (
              <div className="rounded-lg border p-3 space-y-2">
                <div className="text-sm font-semibold">Workflow automation</div>
                <div className="grid gap-2">
                  {workflows.map((workflow) => (
                    <Button key={workflow.name} variant="outline" size="sm" onClick={() => runWorkflow(workflow.name)} disabled={loading}>
                      {workflow.title}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Workflows generate tenant-scoped summaries/reminders. They do not silently mutate records.</p>
              </div>
            )}
            <select className="w-full rounded-md border bg-background p-2" value={selectedTool} onChange={(e) => setSelectedTool(e.target.value)}>
              {Object.entries(groupedTools).map(([category, categoryTools]) => (
                <optgroup key={category} label={category}>
                  {categoryTools.map((tool) => <option key={tool.name} value={tool.name}>{tool.mode === "write" ? "✎ " : ""}{tool.name}</option>)}
                </optgroup>
              ))}
            </select>
            {activeTool && (
              <div className="text-sm text-muted-foreground space-y-2">
                <p>{activeTool.description}</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant={activeTool.mode === "write" ? "default" : "outline"}>{activeTool.mode}</Badge>
                  {activeTool.requires_confirmation && <Badge variant="destructive">requires confirmation</Badge>}
                  {activeTool.required_roles?.length > 0 && <Badge variant="secondary">roles: {activeTool.required_roles.join(", ")}</Badge>}
                </div>
              </div>
            )}
            <Textarea className="font-mono text-xs min-h-[180px]" value={toolInput} onChange={(e) => setToolInput(e.target.value)} />
            <Button onClick={runTool} disabled={loading || !activeSessionId} className="w-full">
              <Play className="mr-2 h-4 w-4" /> Execute {activeTool?.mode === "write" ? "Write" : "Read"} Tool
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OperatorPage;
